import { Landmark } from "lucide-react";
import { useCanAccess, useTranslate } from "ra-core";
import { Link, useLocation } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { ChargeList } from "./charges/ChargeList";
import { ContractList } from "./contracts/ContractList";
import { ExpensesView } from "./expenses/ExpensesView";
import { FinanceOverview } from "./overview/FinanceOverview";

const BASE_PATH = "/finance";

const TABS = [
  { key: "overview", path: BASE_PATH },
  { key: "contracts", path: `${BASE_PATH}/contracts` },
  { key: "charges", path: `${BASE_PATH}/charges` },
  { key: "expenses", path: `${BASE_PATH}/expenses` },
] as const;

type FinanceTab = (typeof TABS)[number]["key"];

const getTab = (pathname: string): FinanceTab =>
  TABS.find((tab) => tab.path !== BASE_PATH && pathname.startsWith(tab.path))
    ?.key ?? "overview";

const FinanceTabs = ({ active }: { active: FinanceTab }) => {
  const translate = useTranslate();
  return (
    <nav
      className="grid w-full grid-cols-4 rounded-lg bg-muted p-1 md:inline-flex md:w-auto"
      aria-label={translate("crm.finance.title")}
    >
      {TABS.map((tab) => (
        <Link
          key={tab.key}
          to={tab.path}
          aria-current={tab.key === active ? "page" : undefined}
          className={cn(
            "truncate rounded-md px-1 py-1 text-center text-xs font-semibold transition-colors md:px-3 md:text-sm",
            tab.key === active
              ? "bg-background text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {translate(`crm.finance.tabs.${tab.key}`)}
        </Link>
      ))}
    </nav>
  );
};

const CONTENT: Record<FinanceTab, () => React.ReactElement> = {
  overview: () => <FinanceOverview />,
  contracts: () => <ContractList />,
  charges: () => <ChargeList />,
  expenses: () => <ExpensesView />,
};

/** Finance: overview dashboard, contracts, receivables and payables (admins only) */
export const FinancePage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { canAccess, isPending } = useCanAccess({
    resource: "finance_charges",
    action: "list",
  });
  const active = getTab(location.pathname);
  // UX only: RLS returns no finance row to a non admin anyway
  const content = isPending ? null : canAccess ? (
    CONTENT[active]()
  ) : (
    <p className="py-12 text-center text-sm text-muted-foreground">
      {translate("crm.finance.admin_only")}
    </p>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex min-w-0 flex-1">
            <h1 className="text-xl font-semibold">
              {translate("crm.finance.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="flex flex-col gap-3 pb-6">
            <FinanceTabs active={active} />
            {content}
          </div>
        </MobileContent>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 py-2">
      <div className="flex items-center gap-4">
        <FinanceTabs active={active} />
        <h1 className="flex items-center gap-2 text-lg font-semibold text-muted-foreground">
          <Landmark className="size-5" />
          {translate("crm.finance.title")}
        </h1>
      </div>
      {content}
    </div>
  );
};

FinancePage.path = BASE_PATH;
